import { styled, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { FLOW2_CATEGORY_GOALS, iwsGoalTypeMapper, TermTabType, FEATURE_NAMES } from '../../common/constants';
import { useAppDispatch, useGoalTermTabs, useIsMultiPortfolioTenant } from '../../common/hooks';
import { Flow2GoalTypeProps } from '../../common/types/clients-entity';
import { Icon, Typography } from '../../components/atoms';
import { NoGoal, PrimaryTabs } from '../../components/molecules';
import { AdviceGoalPayloadType } from '../../features/client-goals';
import { ADVISOR_ROUTES } from '../../routes/routeConstants';
import { ContainerBox, ContentContainer, GoalTypeIcon, GoalTypeIconSmall } from '../../themes/styles/common';
import { calculateYearsBetweenDates, getGoalCountByTerm, checkFFEnabled } from '../../utils/index';
import { GoalSummaryChipFlow2 } from '../../components/molecules/GoalSummaryChipFlow2/GoalSummaryChipFlow2';
import { GoalsInlineTable } from '../../containers/goals-inline-flow2/GoalsInlineTable';
import { WealthGoals } from '../../containers/goal-drawers-flow2/wealth-goal-drawer';
import { RetirementGoals } from '../../containers/goal-drawers-flow2/retirement-goal-drawer';
import { IncomeGoals } from '../../containers/goal-drawers-flow2/income-goal-drawer';
import { initializePlanState } from '../../features/global/globalSlice';

const GoalsContainer = styled('div')(() => ({
    display: 'flex',
    flexDirection: 'column'
}));
const PageContainer = styled('div')(() => ({
    width: '100%',
    position: 'relative',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    marginTop: '16px'
}));
const DisplayRow = styled('div')(() => ({
    display: 'flex',
    flexDirection: 'row'
}));
const GoalsChipsSection = styled('div')(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    columnGap: theme.spacing(2),
    rowGap: '25px',
    marginTop: '6px'
}));
const GoalTypeContainer = styled('div')(() => ({
    display: 'flex',
    gap: '24px',
    alignItems: 'center'
}));

export const CreateGoalsStep = ({
    goalsWithMissingDates
}: {
    goalsWithMissingDates: AdviceGoalPayloadType['formFields'];
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const { selectedTermIndex, displayedFormFields, handleGoalTermTabChange, formFields } = useGoalTermTabs();
    // AEX-3108: single-portfolio tenants have no Short/Medium/Long terms, so no pills and one flat list.
    const isMultiPortfolioTenant = useIsMultiPortfolioTenant();
    const visibleGoals = isMultiPortfolioTenant ? displayedFormFields : formFields;
    const dispatch = useAppDispatch();
    const [showSelectedGoalTypeDrawer, setShowSelectedGoalTypeDrawer] = useState<any>();
    const [showRetirementGoalDrawer, setShowRetirementGoalDrawer] = useState<any>();
    const [showIncomeGoalDrawer, setShowIncomeGoalDrawer] = useState<any>();
    const [currentGoalData, setCurrentGoalData] = useState<any>();
    const [yearsInRetirement, setYearsInRetirement] = useState('0');

    useEffect(() => {
        if (location.pathname !== ADVISOR_ROUTES.GOALS) navigate(ADVISOR_ROUTES.GOALS, { replace: true });
    }, []);

    const goalDeleted = () => {
        // GoalSummaryChipFlow2's delete handler already surgically prunes the deleted goal from
        // every downstream step (request/response/EWS/goal-calculator), flags the deleted goal's
        // term in affectedEwsTerms so the remaining goals in that term re-run EWS, and persists it.
        // Here we only reset the transient plan UI state.
        dispatch(initializePlanState({}));
    };

    const handleEditGoalClick = (goals: AdviceGoalPayloadType['formFields'][0]) => {
        let data = {};
        if (goals.data.type === 'wealth') {
            data = {
                goalAmount: goals?.data?.goalAmount || goals?.data?.targetedRetirementIncome,
                goalId: goals.data?.goalId,
                goalPriority: goals.data?.goalPriority,
                goalName: goals.data?.goalName,
                targetDate: goals?.data?.goalDate || goals?.data?.targetDate,
                title: goals?.data?.wealthGoalType,
                drawerTitle: FLOW2_CATEGORY_GOALS.find((g) => g.type === goals.data.type)?.drawerTitle,
                ...goals.data
            };
        } else {
            const yearsInRetirement = calculateYearsBetweenDates(
                goals?.data?.planStartRetirement,
                goals?.data?.targetDate
            );
            setYearsInRetirement(yearsInRetirement);
            data = {
                goalAmount: goals?.data?.goalAmount,
                goalId: goals?.data?.goalId,
                goalPriority: goals?.data?.goalPriority,
                targetedRetirementIncome: goals?.data?.targetedRetirementIncome,
                goalName: goals?.data?.goalName,
                yearsInRetirement: yearsInRetirement,
                planStartRetirement: goals?.data?.planStartRetirement,
                targetDate: goals?.data?.targetDate,
                frequency: 'Annually',
                title: goals?.data?.wealthGoalType,
                drawerTitle: FLOW2_CATEGORY_GOALS.find((g) => g.type === goals.data.type)?.drawerTitle,
                ...goals.data
            };
        }
        setCurrentGoalData(data);
        if (goals.data.type === 'wealth') setShowSelectedGoalTypeDrawer(goals.data);
        if (goals.data.type === 'retirement') setShowRetirementGoalDrawer(goals.data);
        if (goals.data.type === 'income') setShowIncomeGoalDrawer(goals.data);
    };

    // Goals Inline Editing Revamp (AEX-3446) — Flow2 only. When enabled, replace the legacy
    // drawer-based Goals UI with the new inline editable table. Old flow untouched when off.
    if (checkFFEnabled(FEATURE_NAMES['GOALS_VIEW_REVAMP_FE_AEX-3428'])) {
        return (
            <PageContainer>
                <GoalsInlineTable />
            </PageContainer>
        );
    }

    return (
        <>
            <PageContainer>
                <DisplayRow sx={{ gap: '40px', marginBottom: '16px', padding: '12px 0' }}>
                    <GoalsContainer>
                        <Typography
                            label={t('ADD_GOAL_TYPE')}
                            variant="body.largeBold"
                            marginBottom="16px"
                            color="neutral.black"
                        />
                        <GoalTypeContainer>
                            {FLOW2_CATEGORY_GOALS.map((g: Flow2GoalTypeProps, key) => {
                                return (
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            cursor: 'pointer'
                                        }}
                                        key={key}
                                    >
                                        <GoalTypeIcon
                                            key={key}
                                            color={g.color}
                                            backgroundColor={g.backgroundColor}
                                            onClick={() => {
                                                if (g.type === iwsGoalTypeMapper.retirement) {
                                                    setShowRetirementGoalDrawer(g);
                                                    return;
                                                }
                                                if (g.type === iwsGoalTypeMapper.income) {
                                                    setShowIncomeGoalDrawer(g);
                                                    return;
                                                }
                                                setShowSelectedGoalTypeDrawer(g);
                                            }}
                                        >
                                            <Icon id={g.type} size={40} />
                                        </GoalTypeIcon>
                                        <Typography
                                            label={t(g.heading)}
                                            variant="labels.large"
                                            sx={{ color: theme.palette.neutral.black }}
                                            mt="8px"
                                        />
                                    </div>
                                );
                            })}
                        </GoalTypeContainer>
                    </GoalsContainer>
                </DisplayRow>
                <ContainerBox>
                    {formFields?.length > 0 ? (
                        <>
                            {isMultiPortfolioTenant && (
                                <PrimaryTabs
                                    value={selectedTermIndex}
                                    onChange={handleGoalTermTabChange}
                                    tabItems={[
                                        {
                                            key: TermTabType.SHORT,
                                            label: t('SHORT'),
                                            count: getGoalCountByTerm(formFields, TermTabType.SHORT)
                                        },
                                        {
                                            key: TermTabType.MEDIUM,
                                            label: t('MEDIUM'),
                                            count: getGoalCountByTerm(formFields, TermTabType.MEDIUM)
                                        },
                                        {
                                            key: TermTabType.LONG,
                                            label: t('LONG'),
                                            count: getGoalCountByTerm(formFields, TermTabType.LONG)
                                        }
                                    ]}
                                />
                            )}

                            <ContentContainer isTabIncluded={isMultiPortfolioTenant}>
                                {visibleGoals && visibleGoals.length > 0 ? (
                                    <GoalsChipsSection>
                                        {visibleGoals.map((goals: any, key) => {
                                            const gSpecs = FLOW2_CATEGORY_GOALS.find(
                                                (g) => goals?.data?.type === g.type
                                            );
                                            const isRetirement = goals?.data?.type === iwsGoalTypeMapper.retirement;
                                            const isIncomeGoal = goals?.data?.type === iwsGoalTypeMapper.income;

                                            let targetDate = goals?.data?.goalDate || goals?.data?.targetDate;
                                            let planningDate = null;
                                            let goalAmount = goals?.data?.goalAmount;
                                            if (isRetirement) {
                                                targetDate = goals?.data?.planStartRetirement;
                                                planningDate = dayjs(goals?.data?.targetDate, 'DD-MM-YYYY').format(
                                                    'MMM YYYY'
                                                );
                                            }
                                            if (isIncomeGoal) {
                                                targetDate = goals?.data?.incomeStartDate;
                                                planningDate = dayjs(goals?.data?.targetDate, 'DD-MM-YYYY').format(
                                                    'MMM YYYY'
                                                );
                                                goalAmount = 0;
                                            }
                                            let isMissingDates = goalsWithMissingDates?.find(
                                                (g: { goalId: any }) => g.goalId === goals.goalId
                                            );
                                            const retirementIncome = isIncomeGoal
                                                ? goals?.data?.goalAmount
                                                : goals?.data?.targetedRetirementIncome || 0;

                                            const showErrorBorder = isMissingDates;
                                            // console.log(goals);
                                            return (
                                                <GoalSummaryChipFlow2
                                                    key={goals.goalId}
                                                    headerIcon={
                                                        <GoalTypeIconSmall
                                                            key={key}
                                                            color={gSpecs?.color}
                                                            backgroundColor={gSpecs?.backgroundColor}
                                                        >
                                                            <Icon id={gSpecs?.type || 'income'} size={24} />
                                                        </GoalTypeIconSmall>
                                                    }
                                                    goalType={goals?.data?.type}
                                                    goalId={goals?.data?.goalId}
                                                    onGoalDelete={goalDeleted}
                                                    planningDate={planningDate}
                                                    onEdit={() => handleEditGoalClick(goals)}
                                                    goal_name={goals?.data?.goalName}
                                                    priority={goals?.data?.goalPriority}
                                                    goalAmount={goalAmount}
                                                    targetedRetirementIncome={retirementIncome}
                                                    target_date_data={dayjs(targetDate, 'DD-MM-YYYY').format(
                                                        'MMM YYYY'
                                                    )}
                                                    isRetirement={isRetirement}
                                                    isIncomeGoal={isIncomeGoal}
                                                    showErrorBorder={showErrorBorder}
                                                    frequency={goals?.data?.frequency}
                                                />
                                            );
                                        })}
                                    </GoalsChipsSection>
                                ) : (
                                    <NoGoal
                                        title={t(
                                            selectedTermIndex === TermTabType.SHORT
                                                ? 'NO_GOALS_SHORT'
                                                : selectedTermIndex === TermTabType.MEDIUM
                                                ? 'NO_GOALS_MEDIUM'
                                                : 'NO_GOALS_LONG'
                                        )}
                                        infoText={t('NO_GOALS_TAB_INFOTEXT')}
                                    />
                                )}
                            </ContentContainer>
                        </>
                    ) : (
                        <NoGoal title={t('NO_GOALS')} infoText={t('NO_GOALS_INFOTEXT')} />
                    )}
                </ContainerBox>
            </PageContainer>
            {currentGoalData ? (
                <>
                    <RetirementGoals
                        selectedGoal={showRetirementGoalDrawer}
                        handleClose={() => {
                            setCurrentGoalData(undefined);
                            setShowRetirementGoalDrawer(undefined);
                        }}
                        yearsInRetirement={parseInt(yearsInRetirement)}
                        open={Boolean(showRetirementGoalDrawer)}
                        goalData={currentGoalData}
                    />
                    <IncomeGoals
                        selectedGoal={showIncomeGoalDrawer}
                        handleClose={() => {
                            setCurrentGoalData(undefined);
                            setShowIncomeGoalDrawer(undefined);
                        }}
                        open={Boolean(showIncomeGoalDrawer)}
                        goalData={currentGoalData}
                    />
                    <WealthGoals
                        selectedGoal={showSelectedGoalTypeDrawer}
                        handleClose={() => {
                            setCurrentGoalData(undefined);
                            setShowSelectedGoalTypeDrawer(undefined);
                        }}
                        open={Boolean(showSelectedGoalTypeDrawer)}
                        goalData={currentGoalData}
                    />
                </>
            ) : (
                <>
                    <WealthGoals
                        selectedGoal={showSelectedGoalTypeDrawer}
                        handleClose={() => setShowSelectedGoalTypeDrawer(undefined)}
                        open={Boolean(showSelectedGoalTypeDrawer)}
                    />
                    <RetirementGoals
                        selectedGoal={showRetirementGoalDrawer}
                        handleClose={() => setShowRetirementGoalDrawer(undefined)}
                        open={Boolean(showRetirementGoalDrawer)}
                    />
                    <IncomeGoals
                        selectedGoal={showIncomeGoalDrawer}
                        handleClose={() => setShowIncomeGoalDrawer(undefined)}
                        open={Boolean(showIncomeGoalDrawer)}
                    />
                </>
            )}
        </>
    );
};
